import React, {Suspense} from 'react';
import { defer, useLoaderData, Await } from "react-router-dom";
import LoadingScreen from './LoadingScreen';
import {createLinkFormatter, createNumberFormatter, createPercentFormatter} from './TableUtils.js';
import './TableUtils.css';

import 'react-tabulator/lib/styles.css';
import 'react-tabulator/lib/css/tabulator.min.css';
import { ReactTabulator } from 'react-tabulator'

const linkCol = (title, field, hrefFn) => ({
  title,
  field,
  formatter: createLinkFormatter(hrefFn),
});

function getProblemTypes() {
  const txnHref = (field) => (cell) => `/transactions?${field}=${cell.getValue()}`;
  const idHref = (cell) => `/transactions?id=${cell.getValue()}`;

  return [
    {
      key: 'unmatched_raw_transactions',
      title: 'Unmatched Raw Transactions',
      columns: [
        linkCol("Raw Txn ID", "raw_transaction_record_id",
            (cell) => `/raw_transaction/${cell.getValue()}`),
        linkCol("File", "filename",
            (cell) => `/file/${cell.getData().raw_file_record_id}`),
        { title: "Day", field: "day" },
        linkCol("Create Process", "create_process",
            (cell) => `/parsing/${cell.getValue()}`),
      ],
    },
    {
      key: 'possible_bad_dedupes',
      title: 'Possible Bad Dedupes',
      columns: [
        linkCol("Dup ID", "dup_id", idHref),
        linkCol("Base ID", "base_id", idHref),
        { title: "Type", field: "type" },
        { title: "Day", field: "day" },
        linkCol("From Account", "from_account", txnHref("account")),
        linkCol("To Account", "to_account", txnHref("account")),
        linkCol("From Symbol", "from_symbol", txnHref("symbol")),
        linkCol("To Symbol", "to_symbol", txnHref("symbol")),
        { title: "From Qty", field: "from_quantity", formatter: createNumberFormatter(2), hozAlign: 'right' },
        { title: "To Qty", field: "to_quantity", formatter: createNumberFormatter(2), hozAlign: 'right' },
        { title: "File", field: "file" },
      ],
    },
    {
      key: 'missing_symbols',
      title: 'Missing Symbols',
      columns: [
        linkCol("Symbol", "symbol", txnHref("symbol")),
      ],
    },
    {
      key: 'unallocated_symbols',
      title: 'Unallocated Symbols',
      columns: [
        linkCol("Symbol", "symbol", txnHref("symbol")),
        { title: "Allocated %", field: "allocated_pct", formatter: createPercentFormatter(1), hozAlign: 'right' },
      ],
    },
    {
      key: 'possible_one_sided_transfers',
      title: 'Possible One-Sided Transfers',
      columns: [
        { title: "Day", field: "ht1_day" },
        linkCol("Account", "ht1_account", txnHref("account")),
        { ...linkCol("Symbol", "symbol"),
          formatter: createLinkFormatter((cell) => {
            const account = cell.getData().ht1_account;
            const symbol = cell.getValue();
            return `/transactions?account=${account}&symbol=${symbol}`;
          }),
        },
        { title: "Quantity", field: "quantity", formatter: createNumberFormatter(2), hozAlign: 'right' },
        { title: "Type", field: "ht1_type" },
        linkCol("ID", "ht1_id", idHref),
        { title: "Other Day", field: "ht2_day" },
        linkCol("Other Account", "ht2_account", txnHref("account")),
        { title: "Other Type", field: "ht2_type" },
        linkCol("Other ID", "ht2_id", idHref),
      ],
    },
  ];
}

let _problemsCache = null;
const getProblems = async () => {
  if (_problemsCache) return _problemsCache;
  const url = (process.env.NODE_ENV === 'development' ? '/problems.json' : '/problems');
  _problemsCache = await fetch(url).then((res) => res.json());
  return _problemsCache;
}

export const loader = async ({ params }) => {
  if (_problemsCache) return {problems: _problemsCache};
  const problems = getProblems();
  return defer({problems});
}

const problemTypes = getProblemTypes();

function Problems() {
  const loaderData = useLoaderData();

  return (
    <Suspense fallback={<LoadingScreen visible="true" />}>
      <Await resolve={loaderData.problems}
             errorElement={<div>Error Loading</div>}>
        {(problems) => (
          <div className="problems">
            <h3>Summary</h3>
            <ReactTabulator
              options={{
                data: problemTypes.map((pt) => ({
                  problem: pt.title,
                  count: (problems[pt.key] || []).length,
                })),
                layout: "fitColumns",
              }}
              columns={[
                { title: "Problem", field: "problem" },
                { title: "Count", field: "count", hozAlign: 'right', width: 100 },
              ]}
            />

            {problemTypes.filter((pt) => (problems[pt.key] || []).length > 0).map((pt) => (
              <div key={pt.key}>
                <h3>{pt.title} ({problems[pt.key].length})</h3>
                <ReactTabulator
                  options={{
                    data: problems[pt.key],
                    layout: "fitColumns",
                  }}
                  columns={pt.columns}
                />
              </div>
            ))}
          </div>
        )}
      </Await>
    </Suspense>
  );
}

export default Problems;
